import {
	Alert,
	Badge,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { generatePath, useParams } from "../../../../../../router-utils"
import { TAutomation } from "../../../../../../Automations"

type TFilters = TSearchParams & {
	channel: "whatsapp"
}

export default function WhatsAppIntegrationAutomationsPage() {
	const { integrationId } = useParams(
		"/integrations/:integrationId/whatsapp/automations"
	)
	const [query, setQuery] = useLocationQuery<TFilters, TFilters>()
	const [searchParams, setSearchParams] = useSearch<TFilters>(query)

	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])

	const newAutomationPath = generatePath(
		"/integrations/:integrationId/whatsapp/automations/new",
		{ integrationId }
	)

	return (
		<>
			<Helmet>
				<title>Automations | WhatsApp Integration</title>
			</Helmet>

			<Search
				title="WhatsApp Automations"
				initialParams={searchParams}
				onSearch={(params) => {
					setSearchParams({ ...params, page: 1 })
				}}
				actions={
					<ButtonLink to={newAutomationPath} level="primary">
						Create WhatsApp Automation
					</ButtonLink>
				}
			>
				{() => (
					<ListView<TAutomation, TFilters>
						pageKey={`integrations/${integrationId}/whatsapp/automations`}
						params={{ ...searchParams, channel: "whatsapp" }}
						onPageChange={(page: number) =>
							setSearchParams({ ...searchParams, page })
						}
						fetch={async (xhr, params) => {
							return xhr
								.get("/automations", { params })
								.then((resp) => resp.data)
						}}
					>
						{({ items }: { items: TAutomation[] }) =>
							items.length === 0 ? (
								<Alert status="info">
									<Stack gap="2">
										<Text fontWeight="semibold">No Automations Found</Text>
										<Text>
											No WhatsApp-capable automations match your filters. Create
											the first one to get started.
										</Text>
									</Stack>
								</Alert>
							) : (
								<Table
									bordered
									striped
									responsive
									headers={["Automation", "Source", "Status", "Created", ""]}
									alignCols={{ 4: "right" }}
									rows={items.map((automation) => [
										<Stack gap="1" key={automation.id}>
											<Link
												to={generatePath("/automations/:automationId", {
													automationId: String(automation.id),
												})}
												anchored
												color="accent"
											>
												<Text fontWeight="semibold">
													{automation.template?.name ||
														automation.source?.name ||
														automation.source_key}
												</Text>
											</Link>
											<Inline gap="2" flexWrap="wrap">
												<Badge>{automation.start_type}</Badge>
											</Inline>
										</Stack>,
										<Text>
											{automation.source?.name || automation.source_key}
										</Text>,
										<Badge
											success={automation.is_active}
											warning={!automation.is_active}
										>
											{automation.is_active ? "Active" : "Inactive"}
										</Badge>,
										<RelativeTime timestamp={automation.created_at} />,
										automation.template_key ? (
											<ButtonLink
												to={generatePath("/automations/:automationId/edit", {
													automationId: String(automation.id),
												})}
												size="sm"
												level="tertiary"
												title="Edit Automation"
											>
												<Icons.Pencil />
											</ButtonLink>
										) : null,
									])}
								/>
							)
						}
					</ListView>
				)}
			</Search>
		</>
	)
}
